import React, { PureComponent } from "react";
import styled from "../../../theme";
import { FadeLoader } from "react-spinners";
import Sort from "../sort";
import SortTableRow from "./sort_table_row";
import Accordion from "../accordion/Accordion";

// Base components (keep original imports)
import GridTable from "../../../ui_components/gridTable/GridTable";
import GridTableBody from "../../../ui_components/gridTable/GridTableBody";
import GridTableRow from "../../../ui_components/gridTable/GridTableRow";
import GridTableCell from "../../../ui_components/gridTable/GridTableCell";

const Container = styled.div`
  /* ignore */
`;

/** Renamed styled variants to avoid shadowing */
const StyledGridTable = styled(GridTable)`
  border: 0;
`;

const StyledGridTableBody = styled(GridTableBody)`
  height: calc(100vh - 240px) !important;
  overflow: auto !important;
`;

interface HeaderRowProps {
  scrollWidth: number;
}

const HeaderRow = styled(GridTableRow)<HeaderRowProps>`
  grid-template-columns: 2fr 1fr 1fr;
  grid-template-rows: auto !important;
  grid-template-areas: "displayName email phone";
  grid-column-gap: 0 !important;
  font-weight: 600 !important;
  color: #4a4a4a !important;
  text-align: left !important;
  background-color: #ecf6f9 !important;
  padding-right: 10px !important;
`;

const HeaderCell = styled(GridTableCell)`
  padding: 20px 10px !important;
  border-left: 1px solid #e2e2e2 !important;
  color: #222222 !important;
  font-weight: 500 !important;
  font-size: 14px !important;

  &:first-child {
    border-left: none !important;
  }
`;

export interface SortTableRowInfo {
  id: number;
  active: boolean;
  avatarId?: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  cellPhone?: string;
  homePhone?: string;
  officePhone?: string;
  subList?: SortTableRowInfo[];
  billToParent?: boolean;
  parent?: any;
  billingType?: string;
  projectsCount?: number;
}

export interface SortTableProps {
  sort: "asc" | "desc";
  list: SortTableRowInfo[];
  scrollTop?: number;
  onSortingChanged(sort: "asc" | "desc"): void;
  onRowChoosed(id: number, scrollTop?: number): void;
  isLoading?: boolean;
}

interface SortTableState {
  scrollWidth: number;
}

export class SortTable extends PureComponent<SortTableProps, SortTableState> {
  public state: SortTableState = {
    scrollWidth: 0,
  };

  public componentDidMount() {
    window.addEventListener("resize", this.handleResize);

    const e = document.getElementById("grid-table-body");
    if (e && this.props.scrollTop != null) {
      e.scrollTop = this.props.scrollTop;
    }
    this.handleResize();
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  public componentDidUpdate() {
    this.handleResize();
  }

  public render(): JSX.Element {
    const { list, sort, isLoading } = this.props;

    return (
      <Container>
        <FadeLoader
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)", // center correctly
            zIndex: 9999,
            display: !isLoading ? "none" : "block",
          }}
          color="#3B97B1"
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <StyledGridTable>
          <HeaderRow scrollWidth={this.state.scrollWidth}>
            <HeaderCell uiGridArea="displayName" onClick={this.changeSorting}>
              <Sort direction={sort} />
            </HeaderCell>
            <HeaderCell uiGridArea="email">EMAIL</HeaderCell>
            <HeaderCell uiGridArea="phone">PHONE</HeaderCell>
          </HeaderRow>

          <StyledGridTableBody id="grid-table-body">
            <Accordion
              index={[]}
              handleRowChoosed={this.handleRowChoosed}
              items={list}
            />
          </StyledGridTableBody>
        </StyledGridTable>
      </Container>
    );
  }

  /** Kept in case you still need manual recursion elsewhere */
  public renderRow(
    data: SortTableRowInfo,
    index: number,
    level: number = 0
  ): JSX.Element {
    return (
      <React.Fragment key={`row-${data.id}`}>
        <SortTableRow
          data={data}
          level={level}
          onChoosed={this.handleRowChoosed}
        />
        {data.subList &&
          data.subList.map((sub) => {
            if (sub.billToParent === false) {
              return this.renderRow({ ...sub }, index, level + 1);
            }
            const parent = this.props.list[index];
            return this.renderRow(
              {
                ...sub,
                email: parent ? parent.email : undefined,
                cellPhone: parent ? parent.cellPhone : undefined,
              },
              index,
              level + 1
            );
          })}
      </React.Fragment>
    );
  }

  private changeSorting = () => {
    const { onSortingChanged, sort } = this.props;
    onSortingChanged(sort === "asc" ? "desc" : "asc");
  };

  private handleResize = () => {
    const e = document.getElementById("grid-table-body");
    const firstRow = document.getElementsByClassName(
      "grid-table-row"
    )[0] as HTMLElement | null; // relies on base row class

    let scrollWidth = 0;
    if (e && firstRow) {
      scrollWidth = e.offsetWidth - firstRow.offsetWidth;
    }
    if (scrollWidth !== this.state.scrollWidth) {
      this.setState({ scrollWidth });
    }
  };

  private handleRowChoosed = (id: number) => {
    const e = document.getElementById("grid-table-body");
    const scrollTop = e ? e.scrollTop : undefined;
    this.props.onRowChoosed(id, scrollTop);
  };
}
